/**
 * The client-side reading of backend ARCH-036's meeting entitlement.
 *
 * The server used to send `total_meetings` and `consumed_meetings` and let each
 * screen subtract them. Both are gone from the schema, and the four counters that
 * replaced them are private (`model/user.js`, design Decision 7): every user
 * payload now carries one computed `meetings_available` instead, appended by
 * `toPublicJSON`. This module is the only place that decodes it, because the
 * encoding has a trap in it and three screens read the field.
 *
 * **`null` means unlimited, and `0` means none.** `availableMeetingsJSON` on the
 * server returns `null` for Premium because `Infinity` does not survive
 * `JSON.stringify`. Any check written as a bare comparison gets that backwards —
 * `null >= 1` is `false`, so an unlimited account reads as an exhausted one, and
 * `null == 0` is `false`, so a truthiness test reads it as "has some". Responses
 * also carry `zoomCallsUnlimited`, which is what lets a client tell "unlimited"
 * from "the field never arrived".
 */

/**
 * Whether this account's meetings are uncapped.
 *
 * **`meetings_available` wins whenever it is present**, and `zoomCallsUnlimited` is
 * only consulted when the balance is absent entirely. The two are not
 * interchangeable authorities, even though they normally agree: the server's own
 * `utils/meetingEntitlement.js` says the flag "is never consulted by allowance
 * math" and that `null` is "the authoritative unlimited marker (design Decision
 * 6)". `zoomCallsUnlimited` is a response-facing label that a grant translates
 * *from* (`planMeetingsFromPricePlan`), and it stays independently writable on both
 * `User` and `PricePlan` — so the two can diverge, and when they do the balance is
 * the one that decides what a booking will cost.
 *
 * Reading the flag first would mean a client with `zoomCallsUnlimited: true` and a
 * real balance of 3 was told they had no limit, then refused at the fourth booking
 * with a 403 instead of the price of an add-on.
 */
export const hasUnlimitedMeetings = (user) =>
  user?.meetings_available === undefined
    ? Boolean(user?.zoomCallsUnlimited)
    : user.meetings_available === null;

/**
 * The balance as a non-negative integer.
 *
 * Only meaningful for a capped account — callers must test
 * `hasUnlimitedMeetings` first, since unlimited arrives as `null` and would
 * otherwise be counted as zero. A missing or unparseable field is also 0 rather
 * than `NaN`: the old code let `undefined` leak into a comparison, which is how
 * the pre-check on the booking screen silently stopped firing at all.
 */
export const meetingsAvailableCount = (user) => {
  const available = Number(user?.meetings_available);
  return Number.isFinite(available) && available > 0 ? Math.floor(available) : 0;
};

/**
 * Whether a booking of `meetingsNeeded` meetings can be covered right now.
 *
 * A client-side pre-check only — it decides whether to offer the paid add-on
 * before booking. The server enforces the same rule in `spendMeetings` and
 * answers 403 with `{ meetings_required, meetings_available }` when it disagrees,
 * so this may be optimistic (a stale profile) but must never be more permissive
 * in a way that skips the upsell.
 */
export const canAffordMeetings = (user, meetingsNeeded) =>
  hasUnlimitedMeetings(user) || meetingsAvailableCount(user) >= meetingsNeeded;

/** The balance as a label: the count, or "Unlimited". */
export const formatMeetingsAvailable = (user) =>
  hasUnlimitedMeetings(user) ? "Unlimited" : String(meetingsAvailableCount(user));

const REFUSED = "We couldn't book that appointment. Please try again.";
const CONCURRENT = "Your meeting balance is being updated. Please try again.";
const INSUFFICIENT = "You do not have enough meetings available for this booking.";

/**
 * What to tell the user about an `add-appointment` answer that is not a booking.
 *
 * Lives here because the refusal that matters carries entitlement data, and
 * because every caller of `bookAppointment` needs the same reading of it.
 *
 * **The status is not where it looks.** `bookAppointment` uses this repo's
 * `return error` catch pattern, so a refusal resolves rather than throws and the
 * caller is handed an `AxiosError` — which in axios 1.7.4 has no `status` of its
 * own, only `response.status` and `response.data`. Every call site read the 2xx
 * shape (`result.data.message`), so the refusals below arrived as a warning toast
 * with no text in it: the handler ran, and passed `undefined` to it.
 *
 * Backend ARCH-036 added two of these, and they are the reason this exists:
 *   - **403** `{ message, meetings_required, meetings_available }` — the balance
 *     cannot cover the booking. The server is the authority; the client-side
 *     pre-check can disagree with it whenever the cached profile is stale, so this
 *     path has to be reachable rather than treated as unreachable.
 *   - **409** — a concurrent update to the same balance. Retrying is the fix, so
 *     the message must say so rather than reading as a hard failure. This one is
 *     most likely straight after a payment, where the booking races the credit.
 *
 * It deliberately does not test only for those two codes, because the handler also
 * has to keep working for the refusals that predate them and answer **201** with a
 * `message` (already booked at that time, no advisors, no free slot).
 *
 * One case is knowingly left alone: `add-appointment` also answers **200** with
 * `{ message: 'No available advisors. All are blocked.' }` and no appointment,
 * which the screens treat as a success and render as an empty confirmation. That
 * predates ARCH-036 and is parked in `INBOX.md` rather than changed here, so this
 * commit stays the entitlement migration it claims to be.
 *
 * @returns {string|null} `null` when the answer is a booking
 */
export const bookingRefusalMessage = (result) => {
  const status = result?.response?.status ?? result?.status;
  const body = result?.response?.data ?? result?.data;

  if (status === 200) {
    return null;
  }

  if (status === 403) {
    // The two numbers are the whole point of the server sending them: "you do not
    // have enough" is not actionable on a 60-minute booking, where the shortfall
    // may be one meeting rather than all of it.
    const required = Number(body?.meetings_required);
    const available = Number(body?.meetings_available);
    const shortfall =
      Number.isFinite(required) && Number.isFinite(available)
        ? ` This booking needs ${required}, and you have ${available} available.`
        : "";
    return `${body?.message || INSUFFICIENT}${shortfall}`;
  }

  if (status === 409) {
    return body?.message || CONCURRENT;
  }

  return body?.message || REFUSED;
};
